import LocalPharmacy from '@mui/icons-material/LocalPharmacy'
import People from '@mui/icons-material/People'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Message from '@mui/icons-material/Message'
import Business from '@mui/icons-material/Business'
import LocationOn from '@mui/icons-material/LocationOn'
import { default as LoginIcon } from '@mui/icons-material/Login'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Settings from '@mui/icons-material/Settings'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROLE_CA_FLOWS } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { styled } from '@mui/material/styles'
import ClinicHeader from '../ClinicHeader'
import { AvailabilityPageContainer } from '../ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { CADeviceUseFixingComponent } from '../ClinicHomePage/components/CADeviceUseFixingComponent'
import { checkInClinicAssistant } from '../CheckIn/clinicAssistantServices'

// Styled components
const ModernPageContainer = styled(AvailabilityPageContainer)`
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  min-height: 100vh;
  padding-bottom: 40px;
`

const ContentWrapper = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const BrandHeaderCard = styled(Card)`
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(116, 80, 170, 0.2), 0 4px 16px rgba(116, 80, 170, 0.15);
  border: 3px solid rgba(116, 80, 170, 0.3);
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.03) 50%, rgba(255, 255, 255, 1) 100%);
  margin-bottom: 40px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #7450aa 0%, #8b6fd8 50%, #7450aa 100%);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`

const BrandHeaderContent = styled(Box)`
  padding: 40px 32px;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.12) 0%, rgba(116, 80, 170, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid rgba(116, 80, 170, 0.15);
  @media (max-width: 768px) {
    padding: 32px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const BrandName = styled(Typography)`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const ClinicLocationCard = styled(Card)`
  border-radius: 20px;
  border: 3px solid rgba(116, 80, 170, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%);
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(116, 80, 170, 0.12);

  &:hover {
    border-color: rgba(116, 80, 170, 0.5);
    box-shadow: 0 12px 40px rgba(116, 80, 170, 0.25);
    transform: translateY(-6px);
    background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
  }
`

const ClinicLocationContent = styled(CardContent)`
  padding: 24px !important;
`

const LocationName = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`

const LocationAddress = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`

const CheckInButton = styled(Button)`
  width: 100%;
  margin-top: 20px;
  padding: 16px 32px;
  border-radius: 14px;
  font-weight: 700;
  text-transform: none;
  font-size: 16px;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #6779e3 0%, #8492e8 50%, #6779e3 100%);
  background-size: 200% 100%;
  color: white;
  box-shadow: 0 6px 20px rgba(103, 121, 227, 0.4), 0 2px 8px rgba(103, 121, 227, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #404b8e 0%, #6779e3 50%, #404b8e 100%);
    background-size: 200% 100%;
    box-shadow: 0 8px 28px rgba(103, 121, 227, 0.5), 0 4px 12px rgba(103, 121, 227, 0.4);
    transform: translateY(-3px) scale(1.02);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }
`

const SectionCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid rgba(116, 80, 170, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  height: 100%;

  &:hover {
    border-color: rgba(116, 80, 170, 0.3);
    box-shadow: 0 8px 32px rgba(116, 80, 170, 0.15);
    transform: translateY(-4px);
  }
`

const SectionCardContent = styled(CardContent)`
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`

const SectionIcon = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%);
  margin-bottom: 8px;
`

const SectionTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`

const SectionDescription = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
`

const BrandSelectionCard = styled(Card)`
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid rgba(116, 80, 170, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7450aa 0%, #5a3d88 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(116, 80, 170, 0.3);
    box-shadow: 0 8px 32px rgba(116, 80, 170, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

export default function CALandingPageModern({ assistantData }) {
  const navigate = useNavigate()
  const [CAFlowDialogOpen, setCAFlowDialogOpen] = useState(false)
  const [selectedBrandId, setSelectedBrandId] = useState(null)
  const [checkingInClinicId, setCheckingInClinicId] = useState(null)

  const clinicsList = assistantData?.associatedClinicsData || []
  
  // Group clinics by brand
  const clinicGroups = useMemo(() => {
    const groups = {}
    clinicsList.forEach((clinic) => {
      if (clinic?.clinicBrandId) {
        if (!groups[clinic.clinicBrandId]) {
          groups[clinic.clinicBrandId] = []
        }
        groups[clinic.clinicBrandId].push(clinic)
      }
    })
    return groups
  }, [clinicsList])

  // Get brand names
  const brandNames = useMemo(() => {
    const names = {}
    clinicsList.forEach((clinic) => {
      if (clinic?.clinicBrandId && clinic?.clinicBrandName && !names[clinic.clinicBrandId]) {
        names[clinic.clinicBrandId] = clinic.clinicBrandName
      }
    })
    return names
  }, [clinicsList])

  const brandIds = Object.keys(clinicGroups)
  const hasMultipleBrands = brandIds.length > 1
  const currentBrandId = selectedBrandId || (hasMultipleBrands ? null : brandIds[0])
  const currentClinics = currentBrandId ? clinicGroups[currentBrandId] : []

  // Handle checkin - navigate directly to calendar
  const handleCheckIn = async (clinic) => {
    setCheckingInClinicId(clinic.id)
    
    try {
      const receptionRoomId = clinic?.slug
        ? `${clinic.slug}-reception`
        : 'reception'

      await checkInClinicAssistant(
        receptionRoomId,
        `Check-in to ${clinic.name}`,
        clinic.id
      )
      navigate(`${basePath}/${clinic.clinicBrandId}/${clinic.id}/manage-bookings`)
    } catch (error) {
      console.error('Error during checkin:', error)
      // On error, navigate directly to calendar
      navigate(`${basePath}/${clinic.clinicBrandId}/${clinic.id}/manage-bookings`)
    } finally {
      setCheckingInClinicId(null)
    }
  }

  // Handle brand selection
  const handleBrandSelect = (brandId) => {
    setSelectedBrandId(brandId)
  }

  // Auto-redirect if only one brand
  useEffect(() => {
    if (!hasMultipleBrands && brandIds.length === 1) {
      setSelectedBrandId(brandIds[0])
    }
  }, [hasMultipleBrands, brandIds])

  // This component is only for Clinic Assistant portal (/ca-home)
  // Kiosk portal (/kiosk) routing is handled by KioskRoutesHandler
  // Set ROLE_CA_CONFIG with reception-tablet flow for clinic assistant
  const basePath = '/ca-home' // Hardcoded since this component is only for clinic assistant portal
  
  useEffect(() => {
    const roleCaConfig = {
      FLOW_ID: ROLE_CA_FLOWS.FLOW_RECEPTION_TABLET,
    }
    localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(roleCaConfig))
    // Do not redirect - let the user choose from the landing page
    // The landing page will show clinics and let users select what they want to do
  }, [])

  // Show brand selection if multiple brands
  if (hasMultipleBrands && !currentBrandId) {
    return (
      <ModernPageContainer>
        <ClinicHeader heading="Select a Clinic Brand" />
        <ContentWrapper>
          <Grid container spacing={3}>
            {brandIds.map((brandId) => {
              const brandName = brandNames[brandId] || 'Unnamed Brand'
              const clinics = clinicGroups[brandId] || []
              
              return (
                <Grid item xs={12} sm={6} md={4} key={brandId}>
                  <BrandSelectionCard onClick={() => handleBrandSelect(brandId)}>
                    <CardContent sx={{ padding: '32px !important' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
                          color: 'white',
                          marginBottom: '20px',
                        }}
                      >
                        <Business sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography sx={{ fontSize: 24, fontWeight: 700, marginBottom: '8px' }}>
                        {brandName}
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: '#6b7280', marginBottom: '16px' }}>
                        {clinics.length} location{clinics.length !== 1 ? 's' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#7450aa', fontWeight: 600 }}>
                        Select Brand
                        <ArrowForward sx={{ fontSize: 18 }} />
                      </Box>
                    </CardContent>
                  </BrandSelectionCard>
                </Grid>
              )
            })}
          </Grid>
        </ContentWrapper>
      </ModernPageContainer>
    )
  }

  // Main view with checkin and sections
  const currentBrandName = currentBrandId ? brandNames[currentBrandId] : 'Clinic'

  return (
    <ModernPageContainer>
      <ClinicHeader 
        heading={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {currentBrandName && (
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  {currentBrandName}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasMultipleBrands && (
                <Tooltip title="Change Brand">
                  <IconButton
                    onClick={() => setSelectedBrandId(null)}
                    sx={{
                      color: 'rgba(116, 80, 170, 0.7)',
                      '&:hover': { color: 'rgba(116, 80, 170, 1)' },
                    }}
                  >
                    <Business />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Settings">
                <IconButton
                  onClick={() => setCAFlowDialogOpen(true)}
                  sx={{
                    color: 'rgba(116, 80, 170, 0.7)',
                    '&:hover': { color: 'rgba(116, 80, 170, 1)' },
                  }}
                >
                  <Settings />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        }
      />
      
      <div style={{ position: 'absolute', top: '4px', right: '8px' }}>
        <CADeviceUseFixingComponent
          CAFlowDialogOpen={CAFlowDialogOpen}
          setCAFlowDialogOpen={setCAFlowDialogOpen}
        />
      </div>

      <ContentWrapper>
        {/* Checkin Section - Standout Design */}
        {currentClinics.length > 0 && (
          <BrandHeaderCard>
            <BrandHeaderContent>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    marginBottom: '12px', 
                    color: '#1a1a1a',
                    fontSize: { xs: '20px', sm: '24px' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7450aa 0%, #8b6fd8 100%)',
                      boxShadow: '0 0 12px rgba(116, 80, 170, 0.6)',
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.7, transform: 'scale(1.2)' },
                      },
                    }}
                  />
                  Check In to Start
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#4b5563',
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: 500,
                  }}
                >
                  Select a clinic location to check in and access the calendar
                </Typography>
              </Box>
            </BrandHeaderContent>
            <Box sx={{ padding: '32px', background: 'rgba(255, 255, 255, 0.5)' }}>
              <Grid container spacing={3}>
                {currentClinics.map((clinic) => (
                  <Grid item xs={12} sm={6} md={4} key={clinic.id}>
                    <ClinicLocationCard>
                      <ClinicLocationContent>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            marginBottom: '16px',
                            paddingBottom: '16px',
                            borderBottom: '2px solid rgba(116, 80, 170, 0.1)',
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <LocationOn sx={{ color: '#7450aa', fontSize: 28 }} />
                          </Box>
                          <LocationName sx={{ fontSize: '20px', fontWeight: 700 }}>{clinic.name}</LocationName>
                        </Box>
                        {clinic.location?.address && (
                          <LocationAddress sx={{ marginBottom: '8px', fontSize: '15px' }}>
                            {clinic.location.address}
                          </LocationAddress>
                        )}
                        {clinic.location?.city && (
                          <LocationAddress sx={{ fontSize: '15px', fontWeight: 500 }}>
                            {clinic.location.city}
                          </LocationAddress>
                        )}
                        <CheckInButton
                          variant="contained"
                          startIcon={checkingInClinicId === clinic.id ? <CircularProgress size={18} color="inherit" /> : <LoginIcon sx={{ fontSize: 22 }} />}
                          onClick={() => handleCheckIn(clinic)}
                          disabled={checkingInClinicId !== null}
                        >
                          {checkingInClinicId === clinic.id ? 'Checking In...' : 'Check In'}
                        </CheckInButton>
                      </ClinicLocationContent>
                    </ClinicLocationCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </BrandHeaderCard>
        )}

        {/* Common Actions Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '24px', color: '#1a1a1a' }}>
          Quick Access
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <SectionCard
              onClick={() => {
                if (currentClinics.length > 0) {
                  const firstClinic = currentClinics[0]
                  navigate(`${basePath}/${firstClinic.clinicBrandId}/${firstClinic.id}/pharmacy-database`)
                }
              }}
            >
              <SectionCardContent>
                <SectionIcon>
                  <LocalPharmacy sx={{ fontSize: 32, color: '#7450aa' }} />
                </SectionIcon>
                <SectionTitle>Pharmacy Database</SectionTitle>
                <SectionDescription>Manage medications and inventory</SectionDescription>
              </SectionCardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <SectionCard
              onClick={() => {
                if (currentClinics.length > 0) {
                  const firstClinic = currentClinics[0]
                  navigate(`${basePath}/${firstClinic.clinicBrandId}/${firstClinic.id}/patient-list`)
                }
              }}
            >
              <SectionCardContent>
                <SectionIcon>
                  <People sx={{ fontSize: 32, color: '#7450aa' }} />
                </SectionIcon>
                <SectionTitle>Patient Management</SectionTitle>
                <SectionDescription>View and manage patient records</SectionDescription>
              </SectionCardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <SectionCard
              onClick={() => {
                if (currentClinics.length > 0) {
                  const firstClinic = currentClinics[0]
                  navigate(`${basePath}/${firstClinic.clinicBrandId}/${firstClinic.id}/planned-appointments`)
                }
              }}
            >
              <SectionCardContent>
                <SectionIcon>
                  <CalendarToday sx={{ fontSize: 32, color: '#7450aa' }} />
                </SectionIcon>
                <SectionTitle>Upcoming Appointments</SectionTitle>
                <SectionDescription>View scheduled appointments</SectionDescription>
              </SectionCardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <SectionCard
              onClick={() => {
                if (currentClinics.length > 0) {
                  const firstClinic = currentClinics[0]
                  navigate(`${basePath}/${firstClinic.clinicBrandId}/${firstClinic.id}/communications`)
                }
              }}
            >
              <SectionCardContent>
                <SectionIcon>
                  <Message sx={{ fontSize: 32, color: '#7450aa' }} />
                </SectionIcon>
                <SectionTitle>Communication</SectionTitle>
                <SectionDescription>Manage clinic communications</SectionDescription>
              </SectionCardContent>
            </SectionCard>
          </Grid>
        </Grid>
      </ContentWrapper>

    </ModernPageContainer>
  )
}
